import { useState } from 'react';
import { saveCourseAsTemplateApi } from '../../../../api/apiServices';

// Lets an owner/admin/instructor of the course's own organization capture
// its current structure (sections, lectures, quizzes) as a reusable
// template, scoped to that organization -- it then shows up in the
// "Start your course" gallery for anyone in the org creating a new course.
export default function SaveAsTemplateModal({ courseId, organizationId, onClose }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Give the template a name');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await saveCourseAsTemplateApi({ courseId, organizationId, name: name.trim(), description: description.trim() });
      if (!res.success) throw new Error(res.message || 'Failed to save template');
      setSaved(true);
      setTimeout(onClose, 1200);
    } catch (err) {
      console.error('Save as template error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-900">Save as Template</h2>
          <button onClick={onClose} className="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-2xl">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-xs text-slate-500">
            This saves this course's current structure -- sections, lectures, and quizzes -- as a template your
            organization can reuse to start new courses. Future edits to this course won't change the template.
          </p>

          {saved ? (
            <div className="px-4 py-3 rounded-2xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-medium flex items-center gap-2">
              <i className="ri-check-line" /> Template saved
            </div>
          ) : (
            <>
              {error && (
                <div className="px-4 py-3 rounded-2xl border border-rose-200 bg-rose-50 text-rose-600 text-sm font-medium">
                  {error}
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Template Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Standard Onboarding Course"
                  className="w-full px-4 py-3 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:border-teal-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description (optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What is this template for?"
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-200 rounded-2xl text-sm focus:outline-none focus:border-teal-500 resize-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-1">
                <button
                  onClick={onClose}
                  className="px-4 py-2.5 text-sm font-semibold text-slate-600 rounded-xl hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-5 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
                >
                  {saving ? (<><i className="ri-loader-4-line animate-spin" /> Saving...</>) : 'Save Template'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
